import { useCallback, useEffect } from "react";
import { Box, Container, Divider, useTheme } from "@mui/material";
import { GlobalHeader, useDrawerContext } from "@WahooFitness/wahoo-offline-mfe";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
  Plan,
  PricingCardTag,
  WfActionGroup,
  WfButton,
  WfPricingCard,
  WfSubscriptionFeaturesTable,
} from "@WahooFitness/redesignr";
import useChangePlan from "./useChangePlan";
import useSubscriptionDebug from "@/components/Membership/useSubscriptionDebug";
import SubscriptionDebugInfo from "@/components/Membership/SubscriptionDebugInfo";

function ChangePlan() {
  const { i18n } = useLingui();
  const {
    activeTier,
    hasTrialNoPendingSub,
    selectedPlan,
    onPlanSelect,
    coreTrialAvailable,
    buttonLabel,
    proPkg,
    corePkg,
    onChangePlan,
    purchaseInProgress,
    userSubscription,
    customerInfo,
  } = useChangePlan();

  const { palette, zIndex } = useTheme();
  const { debugOpen, handleTierTap, handleDebugClose } = useSubscriptionDebug();
  const { setDrawer, handleClose: handleDrawerClose } = useDrawerContext();

  const closeDebugPanel = useCallback(() => {
    handleDrawerClose();
    handleDebugClose();
  }, [handleDrawerClose, handleDebugClose]);

  useEffect(() => {
    if (!debugOpen) return;
    setDrawer({
      open: true,
      isPersistent: true,
      hideHeader: true,
      hidePuller: true,
      maxHeight: "70vh",
      children: (
        <SubscriptionDebugInfo
          userSubscription={userSubscription}
          customerInfo={customerInfo}
          onClose={closeDebugPanel}
        />
      ),
    });
  }, [debugOpen, userSubscription, customerInfo, closeDebugPanel, setDrawer]);

  // Close the drawer if the user navigates away while the debug panel is open.
  useEffect(() => {
    return () => {
      if (debugOpen) handleDrawerClose();
    };
  }, [debugOpen, handleDrawerClose]);

  function getPricingCardTag(tier: Plan) {
    const currentTier = hasTrialNoPendingSub ? "base" : activeTier;
    if (currentTier === tier) return PricingCardTag.CURRENT_PLAN;
    if (tier === "pro") return PricingCardTag.RECOMMENDED;
    return undefined;
  }

  return (
    // wf-self-scrolling-page suppresses the shell's page-end spacer: this page owns
    // its scroll region and the sticky action group pads its own bottom clearance.
    <Box
      className="wf-self-scrolling-page"
      display="flex"
      flexDirection="column"
      height="100%"
      bgcolor={palette.background.level1}
    >
      {/* Hidden 10-tap debug trigger across the top bar */}
      <Box
        position="fixed"
        top={0}
        left="30%"
        right="30%"
        height={56}
        zIndex={zIndex.appBar + 1}
        onClick={handleTierTap}
        sx={{ userSelect: "none" }}
      />

      <GlobalHeader title={t`Change plan`} />
      <Box flex={1} overflow="auto">
        <Container maxWidth="sm" disableGutters>
          <Box display="flex" flexDirection="column" gap={1.5} px={2} pt={2} pb={2}>
            <WfPricingCard
              tier="pro"
              price={proPkg?.product.priceString ?? ""}
              i18nContext={i18n}
              tag={getPricingCardTag("pro")}
              isActivePlan={selectedPlan === "pro"}
              onClick={
                activeTier !== "pro" || hasTrialNoPendingSub ? () => onPlanSelect("pro") : undefined
              }
            />

            <WfPricingCard
              tier="core"
              price={corePkg?.product.priceString ?? ""}
              i18nContext={i18n}
              trialAvailable={coreTrialAvailable}
              tag={getPricingCardTag("core")}
              isActivePlan={selectedPlan === "core"}
              onClick={activeTier !== "core" ? () => onPlanSelect("core") : undefined}
            />

            <WfPricingCard
              tier="base"
              price="$0.00"
              i18nContext={i18n}
              tag={getPricingCardTag("base")}
              isActivePlan={selectedPlan === "base"}
              onClick={activeTier !== "base" ? () => onPlanSelect("base") : undefined}
            />

            <WfSubscriptionFeaturesTable
              i18nContext={i18n}
              selectedPlan={selectedPlan}
              type="standalone"
            />
          </Box>
        </Container>
      </Box>
      <Divider />
      <WfActionGroup orientation="horizontal" sticky fullWidth maxContentWidth="sm">
        <WfButton
          hierarchy="primary"
          fullWidth
          disabled={!buttonLabel || purchaseInProgress}
          loading={purchaseInProgress}
          size="large"
          onClick={onChangePlan}
        >
          {buttonLabel}
        </WfButton>
      </WfActionGroup>
    </Box>
  );
}

export default ChangePlan;
